import { IsoDateStr, Maybe, TimeZoneId, UnixTimeMillis, ZonedDateTime, ZonedDateTimeStr } from '@csp/csp-common-model';
import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns';
import {
  getTimeZoneId,
  getTimezoneOffset,
  matchTimezoneStr,
  parseIso,
  zonedEndOfDay,
  zonedStartOfDay,
} from '../util/DateUtil';

/**
 * Get the current time as a ZonedDateTime object.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const now = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => ({
  unixTimeMillis: Date.now(),
  zone,
});

/**
 * Get the start of today as a ZonedDateTime object.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const startOfToday = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => zonedStartOfDay(now(zone));

/**
 * Get the end of today as a ZonedDateTime object.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const endOfToday = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => zonedEndOfDay(now(zone));

/**
 * Get the current time - 24 hours as a ZonedDateTime object.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const yesterday = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime =>
  fromUnixTimeMillis(now(zone).unixTimeMillis - hoursToMilliseconds(24), zone);

/**
 * Get the start of yesterday as a ZonedDateTime object.
 * @param {TimeZoneId} [zone]
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const startOfYesterday = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => zonedStartOfDay(yesterday(zone));

/**
 * Get the end of yesterday as a ZonedDateTime object.
 * @param {TimeZoneId} [zone]
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const endOfYesterday = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => zonedEndOfDay(yesterday(zone));

/**
 * Get the current time + 24 hours as a ZonedDateTime object.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const tomorrow = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime =>
  fromUnixTimeMillis(now(zone).unixTimeMillis + hoursToMilliseconds(24), zone);

/**
 * Get the start of tomorrow as a ZonedDateTime object.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const startOfTomorrow = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => zonedStartOfDay(tomorrow(zone));

/**
 * Get the end of tomorrow as a ZonedDateTime object.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const endOfTomorrow = (zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => zonedEndOfDay(tomorrow(zone));

/**
 * Add days to a ZonedDateTime object.
 * @param {ZonedDateTime} zonedDateTime - ZonedDateTime object
 * @param {number} days - Number of days to add, can be negative.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const addDays = (zonedDateTime: ZonedDateTime, days: number): ZonedDateTime => {
  const unixTimeMillis = zonedDateTime.unixTimeMillis + days * hoursToMilliseconds(24);

  return {
    unixTimeMillis,
    zone: zonedDateTime.zone,
  };
};

/**
 * Add hours to a ZonedDateTime object.
 * @param {ZonedDateTime} zonedDateTime - ZonedDateTime object
 * @param {number} hours - Number of hours to add, can be negative.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const addHours = (zonedDateTime: ZonedDateTime, hours: number): ZonedDateTime => {
  const unixTimeMillis = zonedDateTime.unixTimeMillis + hours * hoursToMilliseconds(1);

  return {
    unixTimeMillis,
    zone: zonedDateTime.zone,
  };
};

/**
 * Add minutes to a ZonedDateTime object.
 * @param {ZonedDateTime} zonedDateTime - ZonedDateTime object
 * @param {number} minutes - Number of minutes to add, can be negative.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const addMinutes = (zonedDateTime: ZonedDateTime, minutes: number): ZonedDateTime => {
  const unixTimeMillis = zonedDateTime.unixTimeMillis + minutes * minutesToMilliseconds(1);

  return {
    unixTimeMillis,
    zone: zonedDateTime.zone,
  };
};

/**
 * Add minutes to a ZonedDateTime object.
 * @param {ZonedDateTime} zonedDateTime - ZonedDateTime object
 * @param {number} minutes - Number of minutes to add, can be negative.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const addSeconds = (zonedDateTime: ZonedDateTime, minutes: number): ZonedDateTime => {
  const unixTimeMillis = zonedDateTime.unixTimeMillis + minutes * 1000;

  return {
    unixTimeMillis,
    zone: zonedDateTime.zone,
  };
};

/**
 * Create a ZonedDateTime object from a string like "2020-07-01T09:04:11.597+01:00" or "2020-07-01T09:04:11.597+01:00[Europe/Stockholm]".
 * @param {ZonedDateTimeStr} zonedDateTimeString - Extended ISO 8601 datetime format with timezone
 * @throws Error Will throw an error if not valid ISO.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
function fromZonedDateTimeString(zonedDateTimeString: ZonedDateTimeStr, zone?: string): ZonedDateTime;
function fromZonedDateTimeString(zonedDateTimeString: Maybe<ZonedDateTimeStr>, zone?: string): Maybe<ZonedDateTime>;
function fromZonedDateTimeString(zonedDateTimeString: Maybe<ZonedDateTimeStr>, zone?: string): Maybe<ZonedDateTime> {
  if (zonedDateTimeString !== undefined) {
    const match = matchTimezoneStr(zonedDateTimeString);
    if (match && match.length > 2 && match[1] != null && match[2] != null) {
      return {
        unixTimeMillis: parseIso(match[1]).getTime(),
        zone: zone ?? match[2],
      };
    } else {
      const date = parseIso(zonedDateTimeString);
      const offset = zone ?? getTimezoneOffset(zonedDateTimeString);
      const timezone = offset.includes('00:00') ? 'UTC' : offset;
      return {
        unixTimeMillis: date.getTime(),
        zone: timezone,
      };
    }
  } else {
    return undefined;
  }
}

/**
 * Create a ZonedDateTime object from a string like "2020-07-01".
 * @param {IsoDateStr} isoDateStr - Date in ISO format YYYY-MM-DD
 * @param {boolean} useCurrentTimezone - use curret timezone otherwise UTC is used
 * @throws Error Will throw an error if not valid ISO.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const fromDateString = (isoDateStr: IsoDateStr, useCurrentTimezone?: boolean): ZonedDateTime => {
  const date = parseIso(isoDateStr);
  const zone = useCurrentTimezone ? getTimeZoneId() : 'UTC';
  return {
    unixTimeMillis: date.getTime(),
    zone,
  };
};

/**
 * Create a ZonedDateTime object from unix time in milliseconds.
 * @param {UnixTimeMillis} unixTimeMillis - Unix time in milliseconds.
 * @param {TimeZoneId} [zone] - Optional IANA timezone to use for formatting. Will use the system timezone if not provided.
 * @return {ZonedDateTime} - ZonedDateTime object
 */
const fromUnixTimeMillis = (unixTimeMillis: UnixTimeMillis, zone: TimeZoneId = getTimeZoneId()): ZonedDateTime => ({
  unixTimeMillis,
  zone,
});

export const ZonedDateTimeFactory = {
  now,
  fromZonedDateTimeString,
  fromDateString,
  fromUnixTimeMillis,

  addDays,
  addHours,
  addMinutes,
  addSeconds,

  startOfToday,
  endOfToday,
  yesterday,
  startOfYesterday,
  endOfYesterday,
  tomorrow,
  startOfTomorrow,
  endOfTomorrow,
};
